import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import MiniLocationMap from "@/components/app/MiniLocationMap";
import { fmtDateTime } from "@/lib/adega";

const STATUS = { pending: ["Pendente", "bg-primary text-primary-foreground"], active: ["Ativa", "bg-emerald-500 text-black"], paused: ["Pausada", "bg-muted text-muted-foreground"] };
const ACTION_LABEL = { approve: "Aprovou", pause: "Pausou", reactivate: "Reativou", remove_photo: "Removeu foto", active_until: "Ativa até", free_trial: "Plano gratuito", remove: "Removeu" };

function History({ items }) {
  if (!items?.length) return null;
  return (
    <details className="mt-3 text-[11px] text-muted-foreground">
      <summary className="cursor-pointer">Histórico ({items.length})</summary>
      <ul className="mt-1 space-y-0.5">{items.map((h) => <li key={h.action_id}>{fmtDateTime(h.at)} · {h.admin_nickname}: {ACTION_LABEL[h.action] || h.action}{h.detail ? ` (${h.detail})` : ""}</li>)}</ul>
    </details>
  );
}

function AdegaRow({ a, onChanged }) {
  const [until, setUntil] = useState(a.active_until || "");
  const patch = async (body, msg) => {
    try { await api.patch(`/admin/adegas/${a.id}`, body); toast.success(msg); onChanged(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async () => {
    if (!window.confirm(`Remover a adega "${a.name}" e todo o catálogo?`)) return;
    try { await api.delete(`/admin/adegas/${a.id}`); toast.success("Adega removida."); onChanged(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const [label, cls] = STATUS[a.status] || STATUS.active;
  return (
    <div className="rounded-xl bg-card border border-white/10 p-4" data-testid={`admin-adega-${a.id}`}>
      <div className="flex items-center gap-2 flex-wrap text-xs mb-1">
        <Badge className={cls} data-testid={`admin-adega-status-${a.id}`}>{label}</Badge>
        {a.expired && <Badge variant="destructive" data-testid={`admin-adega-expired-${a.id}`}>Vencida</Badge>}
        {a.needs_review && <Badge variant="outline" className="border-primary text-primary" data-testid={`admin-adega-review-${a.id}`}>Endereço alterado</Badge>}
        <span className="text-muted-foreground">{a.products_count} produto{a.products_count === 1 ? "" : "s"}</span>
      </div>
      <div className="font-semibold">{a.name}</div>
      <div className="text-xs text-muted-foreground">Responsável: {a.responsible_name || a.owner_nickname} · {a.owner_email} · WhatsApp {a.whatsapp}</div>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div className="text-xs space-y-1">
          <div className="text-muted-foreground">Endereço digitado</div>
          <div data-testid={`admin-adega-address-${a.id}`}>{a.address}</div>
          {a.approx_only && <div className="text-primary">Mostra só região aproximada</div>}
          <a href={`/?adega=${a.id}`} className="text-primary underline inline-block mt-1" data-testid={`admin-adega-view-map-${a.id}`}>Ver no mapa</a>
        </div>
        <div className="h-32 [&>div]:h-32"><MiniLocationMap lat={a.lat} lng={a.lng} readOnly zoom={16} /></div>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <span className="text-xs whitespace-nowrap">Ativa até</span>
        <Input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className="h-9" data-testid={`admin-adega-until-${a.id}`} />
        <Button size="sm" variant="outline" className="press" onClick={() => patch({ active_until: until }, "Data salva.")} data-testid={`admin-adega-save-until-${a.id}`}>Salvar</Button>
      </div>
      <label className="flex items-center gap-2 mt-2 text-xs">
        <Switch checked={!!a.free_trial} onCheckedChange={(v) => patch({ free_trial: v }, "Plano atualizado.")} data-testid={`admin-adega-trial-${a.id}`} /> Plano gratuito de teste
      </label>
      <div className="flex gap-2 mt-3 flex-wrap">
        {a.status === "pending" && <Button size="sm" className="press" onClick={() => patch({ action: "approve" }, "Adega aprovada.")} data-testid={`admin-adega-approve-${a.id}`}>Aprovar</Button>}
        {a.status === "active" && a.needs_review && <Button size="sm" className="press" onClick={() => patch({ action: "approve" }, "Endereço aprovado.")} data-testid={`admin-adega-reapprove-${a.id}`}>Reaprovar endereço</Button>}
        {a.status === "active" && <Button size="sm" variant="outline" className="press" onClick={() => patch({ action: "pause" }, "Adega pausada.")} data-testid={`admin-adega-pause-${a.id}`}>Pausar</Button>}
        {a.status === "paused" && <Button size="sm" className="press" onClick={() => patch({ action: "reactivate" }, "Adega reativada.")} data-testid={`admin-adega-reactivate-${a.id}`}>Reativar</Button>}
        {a.photo_url && <Button size="sm" variant="outline" className="press" onClick={() => patch({ action: "remove_photo" }, "Foto removida.")} data-testid={`admin-adega-remove-photo-${a.id}`}>Remover foto</Button>}
        <Button size="sm" variant="destructive" className="press" onClick={remove} data-testid={`admin-adega-delete-${a.id}`}>Remover</Button>
      </div>
      <History items={a.history} />
    </div>
  );
}

export default function AdminAdegas() {
  const [list, setList] = useState([]);
  const [tab, setTab] = useState("pending");
  const load = async () => {
    try { const { data } = await api.get("/admin/adegas"); setList(data); } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);
  const shown = list.filter((a) => a.status === tab);
  return (
    <div className="space-y-3" data-testid="admin-adegas">
      <div className="flex gap-2">
        {Object.entries(STATUS).map(([k, [label]]) => (
          <button key={k} onClick={() => setTab(k)} className={`press h-9 px-3 rounded-full text-sm border ${tab === k ? "bg-primary text-primary-foreground border-primary" : "border-white/10"}`} data-testid={`admin-adegas-filter-${k}`}>
            {label}s ({list.filter((a) => a.status === k).length})
          </button>
        ))}
      </div>
      {shown.length === 0 && <p className="text-sm text-muted-foreground">Nenhuma adega aqui.</p>}
      {shown.map((a) => <AdegaRow key={a.id} a={a} onChanged={load} />)}
    </div>
  );
}
